import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/apiRouting';
import TableChartManager from './TableChartManager';

interface Store {
  storeId: string;
  storeName?: string;
  name?: string;
  status?: string;
}

const getDisplayName = (store: Store) =>
  store.storeName || store.name || store.storeId;

const TableChartPage: React.FC = () => {
  const { getIdToken } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStores = async () => {
      try {
        const token = await getIdToken();
        const response = await fetch(`${getApiUrl('stores')}/api/stores`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to load stores');
        const data = await response.json();
        const storeList = Array.isArray(data) ? data : data.stores || [];
        const active = storeList.filter((s: Store) => s.status !== 'inactive');
        setStores(active);
        if (active.length > 0) {
          setSelectedStoreId(active[0].storeId);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load stores');
      } finally {
        setLoading(false);
      }
    };
    loadStores();
  }, [getIdToken]);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#a0a0b8' }}>
        Loading stores...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#f2b4b4' }}>
        {error}
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#a0a0b8' }}>
        No locations found. Create a location first.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <label style={{ color: '#a0a0b8', fontSize: '0.85rem' }}>Location:</label>
        <select
          value={selectedStoreId}
          onChange={(e) => setSelectedStoreId(e.target.value)}
          style={{
            padding: '6px 12px',
            background: '#16213e',
            border: '1px solid #2a2a4a',
            borderRadius: '6px',
            color: '#e0e0e0',
            fontSize: '0.85rem',
          }}
        >
          {stores.map((store) => (
            <option key={store.storeId} value={store.storeId}>
              {getDisplayName(store)}
            </option>
          ))}
        </select>
      </div>
      {selectedStoreId && (
        <TableChartManager
          key={selectedStoreId}
          restaurantId={selectedStoreId}
        />
      )}
    </div>
  );
};

export default TableChartPage;
